import React, { useState, lazy, Suspense, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import DemoPdfPreviewModal from './DemoPdfPreviewModal';
import {
  ArrowLeft,
  Send,
  Camera,
  Mic,
  Clock3,
  UserCircle,
  X,
  DollarSign,
  Info,
  Check,
  Palette,
  Scissors,
  Briefcase,
  Code,
  Utensils,
  GraduationCap,
  Hammer,
  ShoppingBag,
  Package,
  Boxes,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  Trash2,
  PlayCircle,
  Zap,
  Bot,
  Layers,
  Plus,
  Search,
  User,
  Phone,
  MapPin,
  Clock,
  CheckCircle2,
  Lock,
  Pencil
} from 'lucide-react';
import ChatAssistant from './ChatAssistant';
import { useQuoteChat } from '../hooks/useQuoteChat';
import { useQuotePricing } from '../hooks/useQuotePricing';
import { useQuotePersistence } from '../hooks/useQuotePersistence';
import { useTeamPermissions } from '../contexts/TeamPermissionsContext';
import { motion, AnimatePresence } from "motion/react";
import { UserProfile, FinancialConfig, Quote, Material, QuoteProductItem, ProductTemplate, QuoteFormState, SimpleService } from '../types';
import { formatCurrencyBRL } from '../utils/formatUtils';
import { getEffectivePrice } from '../utils/promoUtils';
import LoadingSpinner from './LoadingSpinner';
import PaywallModal from './PaywallModal';
import { TUTORIAL_LINKS } from '../constants';
import { CATEGORIES } from '../constants/categories';

const PDFExporter = lazy(() => import('./PDFExporter'));

const Tooltip = ({ children, text }: { children: React.ReactNode, text: string }) => {
  const [isVisible, setIsVisible] = useState(false);
  return (
    <div 
      className="relative flex items-center"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onClick={() => setIsVisible(!isVisible)}
    >
      {children}
      <AnimatePresence>
        {isVisible && (
          <motion.div 
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-800 text-white text-xs rounded-lg shadow-xl z-50 text-center pointer-events-none"
          >
            {text}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export type QuoteMode = 'direct' | 'ai';

interface ClientContact {
  id?: string;
  name: string;
  phone?: string;
  address?: string;
}

interface QuoteCreatorProps {
  user: UserProfile;
  config: FinancialConfig;
  materials: Material[];
  recipes: ProductTemplate[];
  services?: SimpleService[];
  clients?: ClientContact[];
  onClose: () => void;
  onSaveQuote: (quote: Quote) => void;
  onOpenHistory?: () => void;
  onOpenProfile?: (step?: any) => void;
  onOpenCosts?: () => void;
  quotesCount: number;
  plan: 'Grátis' | 'Pro' | 'Especialista' | 'Empresa' | 'Empresarial' | 'Enterprise';
  initialData?: {
    materialCost?: number;
    timeSpent?: number;
    serviceName?: string;
    serviceDescription?: string;
    clientName?: string;
    clientPhone?: string;
    clientAddress?: string;
  } | null;
  onClearInitialData?: () => void;
}

const isServiceNicheActive = (categoryIndex: number): boolean =>
  CATEGORIES.find(c => c.id === categoryIndex)?.isServiceNiche ?? false;

const getNicheIcon = (niche: string) => {
  const n = niche?.toLowerCase() || '';
  if (n.includes('foto')) return Camera;
  if (n.includes('design') || n.includes('arte')) return Palette;
  if (n.includes('artesanato') || n.includes('costura')) return Scissors;
  if (n.includes('program') || n.includes('dev') || n.includes('tech')) return Code;
  if (n.includes('comida') || n.includes('gastro') || n.includes('doce')) return Utensils;
  if (n.includes('beleza') || n.includes('estética') || n.includes('moda')) return Scissors;
  if (n.includes('aula') || n.includes('educ') || n.includes('ensino')) return GraduationCap;
  if (n.includes('obra') || n.includes('reforma') || n.includes('marcenaria')) return Hammer;
  if (n.includes('loja') || n.includes('venda') || n.includes('comércio')) return ShoppingBag;
  return Briefcase;
};

export default function QuoteCreator({ 
  user, 
  config, 
  materials = [], 
  recipes = [], 
  services = [],
  clients = [],
  onClose, 
  onSaveQuote, 
  onOpenHistory, 
  onOpenProfile, 
  onOpenCosts, 
  quotesCount, 
  plan,
  initialData,
  onClearInitialData
}: QuoteCreatorProps) {
  const defaultMargin = user.actingType === 'Já atuo' ? 40 : 20;

  // --- SELETOR DE MODO (PADRÃO CHAT BOT / CONVERSACIONAL) ---
  const [quoteMode, setQuoteMode] = useState<QuoteMode>('ai');

  const {
    chatSessionKey,
    aiExplanation,
    setAiExplanation,
    budgetReady,
    setBudgetReady,
    resetChat
  } = useQuoteChat({ userId: user.id, initialData });

  const [showExportOptions, setShowExportOptions] = useState(false);
  const [showResetToast, setShowResetToast] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [showDemoPreview, setShowDemoPreview] = useState(false);
  const [catalogFilter, setCatalogFilter] = useState<'all' | 'services' | 'recipes' | 'materials'>('all');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [isEditingFinalPrice, setIsEditingFinalPrice] = useState(false);
  const [manualPriceInput, setManualPriceInput] = useState('');

  const isDemo = user.id === 'guest' || user.businessName === 'R-MAX (Modo Teste)' || user.ownerName === 'Visitante';
  const navigate = useNavigate();

  const { can } = useTeamPermissions();
  const canViewMargin = can('view_margin');
  const canEditFinalPrice = can('edit_final_price');

  const [formData, setFormData] = useState<QuoteFormState>(() => {
    const defaultState: QuoteFormState = {
      clientName: initialData?.clientName || '',
      clientPhone: initialData?.clientPhone || '',
      clientAddress: initialData?.clientAddress || '',
      serviceName: initialData?.serviceName || '',
      serviceDescription: initialData?.serviceDescription || '',
      categoryIndex: 1,
      materialCost: initialData?.materialCost || 0,
      timeHours: initialData ? Math.floor((initialData.timeSpent || 0) / 60) : 0,
      timeMinutes: initialData ? (initialData.timeSpent || 0) % 60 : 0,
      quantity: 1,
      extraCosts: 0,
      deadline: '',
      phone: user.phone || '',
      profitMarginInput: defaultMargin,
      products: [],
      services: []
    };

    if (initialData) return defaultState;

    if (typeof window === 'undefined') return defaultState;
    try {
      const saved = localStorage.getItem(`rmax_draft_form_${user.id}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object') {
          return {
            ...defaultState,
            ...parsed,
            clientName: parsed.clientName || '',
            clientPhone: parsed.clientPhone || '',
            clientAddress: parsed.clientAddress || '',
            serviceName: parsed.serviceName || '',
            serviceDescription: parsed.serviceDescription || '',
            products: Array.isArray(parsed.products) ? parsed.products : [],
            services: Array.isArray(parsed.services) ? parsed.services : []
          };
        }
        localStorage.removeItem(`rmax_draft_form_${user.id}`);
      }
      return defaultState;
    } catch (e) {
      console.error('Error reading draft form state:', e);
      try {
        localStorage.removeItem(`rmax_draft_form_${user.id}`);
      } catch {}
      return defaultState;
    }
  });

  const {
    costPrice,
    finalPrice,
    effectiveMargin,
    effectiveProfit,
    displayMargin,
    hiddenMarkupTotal,
    useOperationalTax,
    hasMathError,
    productivityBreakEven,
    riskAlert,
    loadRecipe,
    buildSnapshotProducts
  } = useQuotePricing({
    formData,
    setFormData,
    user,
    config,
    materials,
    recipes,
    onOpenProfile,
    onOpenCosts,
    setAiExplanation,
    setBudgetReady
  });

  // Garante que no Modo Direto o orçamento esteja sempre pronto para visualização
  useEffect(() => {
    if (quoteMode === 'direct') {
      setBudgetReady(true);
    }
  }, [quoteMode, setBudgetReady]);

  // Limite de orçamentos no plano gratuito (5/mês)
  useEffect(() => {
    const maxAllowed = user?.maxFreeQuotes || 5;
    if (plan === 'Grátis' && quotesCount >= maxAllowed) {
      setIsPaywallOpen(true);
    }
  }, [plan, quotesCount, user?.maxFreeQuotes]);

  // Lista unificada do catálogo
  const catalog = useMemo<{ 
    products: { name: string, unitValue: number, recipeId?: string, isProduct?: boolean, materialId?: string }[], 
    services: { name: string, value: number, id?: string }[],
    recipes: { name: string, id: string, cost: number }[]
  }>(() => {
    const prods: { name: string, unitValue: number, recipeId?: string, isProduct?: boolean, materialId?: string }[] = [];
    const recs: { name: string, id: string, cost: number }[] = [];

    // Receitas e Fichas Técnicas
    (recipes || []).forEach(r => {
      const calculatedRecipeCost = (r.items || []).reduce((acc, item) => {
        const mat = (materials || []).find(m => m.id === item.materialId);
        if (!mat) return acc;
        const unitCost = mat.pricePaid / (mat.yieldAmount || 1);
        return acc + (unitCost * item.quantity);
      }, 0);

      const roundedCost = Math.round(calculatedRecipeCost * 100) / 100;
      recs.push({ name: r.name, id: r.id, cost: roundedCost });
      prods.push({
        name: r.name,
        unitValue: roundedCost,
        recipeId: r.id,
        isProduct: false
      });
    });

    // Insumos e Produtos de Venda
    (materials || []).forEach(m => {
      if (m.itemType === 'produto_venda' || (m.category === 'variável' && (m.salePrice || 0) > 0)) {
        prods.push({
          name: m.name,
          unitValue: m.salePrice || Math.round(m.pricePaid * 1.4 * 100) / 100,
          isProduct: true,
          materialId: m.id
        });
      } else if (m.category === 'variável') {
        prods.push({
          name: m.name,
          unitValue: Math.round((m.pricePaid / (m.yieldAmount || 1)) * 100) / 100,
          isProduct: false,
          materialId: m.id
        });
      }
    });

    const srvs = (services || []).map(s => ({
      name: s.name,
      value: Number(s.value || 0),
      id: s.id
    }));

    return { products: prods, services: srvs, recipes: recs };
  }, [materials, recipes, services]);

  const {
    isSaving,
    saved,
    setSaved,
    hasCreatedQuote,
    networkError,
    handleSaveQuote,
    clearPersistenceDrafts
  } = useQuotePersistence({
    user,
    quotesCount,
    plan,
    formData,
    finalPrice,
    aiExplanation,
    budgetReady,
    onSaveQuote,
    buildSnapshotProducts,
    setIsPaywallOpen,
    initialData,
    onClearInitialData
  });

  const scrollRef = useRef<HTMLDivElement | null>(null);
  
  const updateProduct = useCallback(<K extends keyof QuoteProductItem>(index: number, field: K, value: QuoteProductItem[K]) => {
    setFormData(prev => {
      const list = [...prev.products];
      const item = { ...list[index], [field]: value };
      if (field === 'quantity' || field === 'unitValue') {
        item.total = (Number(item.quantity) || 0) * (Number(item.unitValue) || 0);
      }
      list[index] = item;
      return { ...prev, products: list, finalPrice: undefined };
    });
  }, []);

  const removeProduct = useCallback((index: number) => {
    setFormData(prev => ({ ...prev, products: prev.products.filter((_, i) => i !== index), finalPrice: undefined }));
  }, []);

  const addProduct = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      products: [...prev.products, { name: '', quantity: 1, unitValue: 0, total: 0, isPassThrough: false, showOnPdf: true }],
      finalPrice: undefined
    }));
  }, []);

  const handleAddProductFromCatalog = useCallback((item: { name: string; unitValue: number; materialId?: string; recipeId?: string }) => {
    if (item.recipeId) {
      loadRecipe(item.recipeId);
      return;
    }
    setFormData(prev => ({
      ...prev,
      products: [
        ...prev.products,
        {
          name: item.name,
          quantity: 1,
          unitValue: item.unitValue,
          total: item.unitValue,
          materialId: item.materialId,
          isPassThrough: false,
          showOnPdf: true
        }
      ],
      finalPrice: undefined
    }));
  }, [loadRecipe]);
  
  const updateService = useCallback(<K extends keyof { name: string; value: number }>(index: number, field: K, value: { name: string; value: number }[K]) => {
    setFormData(prev => {
      const list = [...prev.services];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, services: list, finalPrice: undefined };
    });
  }, []);

  const removeService = useCallback((index: number) => {
    setFormData(prev => ({ ...prev, services: prev.services.filter((_, i) => i !== index), finalPrice: undefined }));
  }, []);

  const addService = useCallback(() => {
    setFormData(prev => ({ ...prev, services: [...prev.services, { name: '', value: 0 }], finalPrice: undefined }));
  }, []);

  const handleAddServiceFromCatalog = useCallback((srv: { name: string; value: number }) => {
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, { name: srv.name, value: srv.value }],
      finalPrice: undefined
    }));
  }, []);

  const handleApplyManualFinalPrice = useCallback(() => {
    const rawVal = parseFloat(manualPriceInput.replace(/\./g, '').replace(',', '.'));
    if (!isNaN(rawVal) && rawVal > 0) {
      // Arredondamento obrigatório múltiplo de 5 (Artigo 2 §1)
      const rounded = Math.ceil(rawVal / 5) * 5;
      setFormData(prev => {
        const nextState = { ...prev, finalPrice: rounded };
        // Se a lista de itens estiver vazia, cria uma linha descritiva para compor o orçamento e o PDF
        if ((!nextState.services || nextState.services.length === 0) && (!nextState.products || nextState.products.length === 0)) {
          const itemTitle = nextState.serviceName?.trim() || 'Serviço / Produto Principal';
          nextState.services = [{ name: itemTitle, value: rounded }];
        }
        return nextState;
      });
      setIsEditingFinalPrice(false);
      setBudgetReady(true);
    }
  }, [manualPriceInput, setBudgetReady]);

  const handleSwitchMode = (mode: QuoteMode) => {
    setQuoteMode(mode);
    localStorage.setItem('rmax_quote_mode_preference', mode);
    if (mode === 'direct') {
      setBudgetReady(true);
    }
  };

  const handleHardReset = useCallback((showToast = false) => {
    if (!user.id) return;
    clearPersistenceDrafts();
    setFormData({
      clientName: '', clientPhone: '', clientAddress: '', serviceName: '',
      serviceDescription: '',
      categoryIndex: 1, materialCost: 0, timeHours: 0, timeMinutes: 0,
      quantity: 1, extraCosts: 0, deadline: '', phone: user.phone || '',
      profitMarginInput: defaultMargin, products: [], services: []
    });
    resetChat();
    setQuoteMode('ai');
    setQuoteStep(1);
    setSaved(false);
    setShowExportOptions(false);
    setIsSharing(false);

    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (showToast) {
      setShowResetToast(true);
      setTimeout(() => setShowResetToast(false), 4000);
    }
  }, [user.id, defaultMargin, resetChat, clearPersistenceDrafts, setSaved]);

  const handleManualClose = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  const handlePrepareExport = useCallback(() => {
    if (!(formData?.clientName || '').trim() || !(formData?.serviceName || '').trim() || finalPrice <= 0) return;
    setShowExportOptions(true);
  }, [formData?.clientName, formData?.serviceName, finalPrice]);

  // Lista de clientes filtrada para o autocompletar
  const filteredClients = useMemo(() => {
    const rawClient = (formData?.clientName || '').trim();
    if (!rawClient) return (clients || []).slice(0, 5);
    const q = rawClient.toLowerCase();
    return (clients || []).filter(c => (c.name || '').toLowerCase().includes(q) || (c.phone && c.phone.includes(q))).slice(0, 5);
  }, [clients, formData?.clientName]);

  // Cálculo da Mão de Obra pelo Tempo
  const laborHoursCalculated = (formData.timeHours || 0) + ((formData.timeMinutes || 0) / 60);
  const laborCostFromTime = Math.round(laborHoursCalculated * (config.baseHourValue || 50) * 100) / 100;

  // Wizard 3 Passos (Tela 03)
  const [quoteStep, setQuoteStep] = useState<1 | 2 | 3>(() => {
    return initialData?.clientName ? 2 : 1;
  });

  const itemsSum = useMemo(() => {
    const prodsSum = (formData.products || []).reduce((acc, p) => acc + (Number(p.total) || 0), 0);
    const srvsSum = (formData.services || []).reduce((acc, s) => acc + (Number(s.value) || 0), 0);
    const laborSum = ((formData.timeHours || 0) * 60 + (formData.timeMinutes || 0)) * ((config.baseHourValue || 50) / 60);
    return prodsSum + srvsSum + laborSum;
  }, [formData.products, formData.services, formData.timeHours, formData.timeMinutes, config.baseHourValue]);

  const operationalDifference = Math.max(0, finalPrice - itemsSum);
  const isSumExceedingFinalPrice = itemsSum > finalPrice + 0.50;

  return (
    <div className="min-h-dvh h-full bg-[#F8F9FA] flex flex-col font-sans overflow-hidden print:hidden">
      {/* HEADER PRINCIPAL */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-3 sm:px-4 py-2.5 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <button 
            type="button"
            onClick={handleManualClose} 
            className="p-1.5 hover:bg-zinc-100 rounded-full transition-colors"
            title="Voltar"
          >
            <ArrowLeft className="h-5 w-5 text-zinc-900" />
          </button>
          
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center overflow-hidden ${!user?.logoUrl ? 'bg-brand/5 border border-brand/10' : ''}`}>
              {user?.logoUrl ? (
                <img src={user.logoUrl} alt="Logo" className="w-full h-full object-cover aspect-square" />
              ) : (
                (() => {
                  const NicheIcon = getNicheIcon(user?.niche || 'Geral');
                  return <NicheIcon className="h-4 w-4 text-brand" />;
                })()
              )}
            </div>
            <div className="hidden xs:block">
              <h1 className="text-xs sm:text-sm font-bold text-zinc-900 leading-tight">
                {user?.businessName || 'R-Max Orçamentos'}
              </h1>
              <p className="text-[9px] text-zinc-500 font-medium">
                {quoteMode === 'direct' ? 'Modo Direto' : 'Chat'}
              </p>
            </div>
          </div>
        </div>

        {/* SELETOR CENTRAL DE MODO (MODO DIRETO vs CHAT) */}
        <div className="flex items-center bg-zinc-100 p-0.5 sm:p-1 rounded-2xl border border-zinc-200/80 shadow-xs">
          <button
            type="button"
            onClick={() => handleSwitchMode('direct')}
            className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-xl text-[11px] sm:text-xs font-black transition-all cursor-pointer ${
              quoteMode === 'direct'
                ? 'bg-white text-zinc-900 shadow-xs border border-zinc-200/70'
                : 'text-zinc-500 hover:text-zinc-800'
            }`}
            title="Modo Direto: selecione do catálogo, insira horas e calcule margens em 3 passos"
          >
            <Zap className={`h-3.5 w-3.5 ${quoteMode === 'direct' ? 'text-amber-500 fill-amber-500' : ''}`} />
            <span>Modo Direto</span>
          </button>
          
          <button
            type="button"
            onClick={() => handleSwitchMode('ai')}
            className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-xl text-[11px] sm:text-xs font-black transition-all cursor-pointer ${
              quoteMode === 'ai'
                ? 'bg-brand text-white shadow-xs'
                : 'text-zinc-500 hover:text-zinc-800'
            }`}
            title="Chat: monte o orçamento conversando por texto, áudio ou foto"
          >
            <Zap className={`h-3.5 w-3.5 ${quoteMode === 'ai' ? 'text-amber-300 fill-amber-300' : ''}`} />
            <span>Chat</span>
          </button>
        </div>

        {/* AÇÕES DA BARRA SUPERIOR */}
        <div className="flex items-center gap-1 sm:gap-2">
          <button 
            type="button"
            onClick={() => {
              if (window.confirm('Deseja limpar todos os dados e começar um novo orçamento?')) {
                handleHardReset(true);
              }
            }} 
            className="p-1.5 sm:p-2 text-zinc-400 rounded-full hover:bg-rose-50 hover:text-rose-500 transition-colors"
            title="Limpar formulário"
          >
            <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>

          {onOpenHistory && (
            <button 
              type="button"
              onClick={onOpenHistory} 
              className="p-1.5 sm:p-2 text-zinc-600 rounded-full hover:bg-zinc-100" 
              title="Histórico de Orçamentos"
            >
              <Clock3 className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          )}

          {TUTORIAL_LINKS.orcamento ? (
            <a 
              href={TUTORIAL_LINKS.orcamento}
              target="_blank"
              rel="noopener noreferrer"
              title="Assistir Vídeo Tutorial desta tela"
              className="p-1.5 sm:p-2 bg-blue-50 border border-blue-200 text-blue-600 rounded-xl flex items-center gap-1 hover:bg-blue-100 transition-colors shadow-xs cursor-pointer"
            >
              <PlayCircle className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest hidden md:inline">Tutorial</span>
            </a>
          ) : null}
        </div>
      </header>

      {/* WIZARD 3 PASSOS (TELA 03) */}
      {quoteMode === 'direct' && (
        <div className="bg-white border-b border-zinc-100 px-4 py-3 flex items-center justify-center shrink-0">
          <div className="flex items-center gap-2 sm:gap-6">
            <button
              type="button"
              onClick={() => setQuoteStep(1)}
              className={`flex items-center gap-2 text-xs sm:text-sm font-bold transition-colors cursor-pointer ${
                quoteStep === 1 ? 'text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'
              }`}
            >
              <span className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                formData.clientName.trim() && quoteStep > 1
                  ? 'bg-emerald-600 text-white'
                  : quoteStep === 1
                  ? 'bg-zinc-900 text-white'
                  : 'bg-zinc-100 text-zinc-500 border border-zinc-200'
              }`}>
                {formData.clientName.trim() && quoteStep > 1 ? <Check className="w-4 h-4" /> : '1'}
              </span>
              <span>Cliente</span>
            </button>

            <span className="w-6 sm:w-12 h-0.5 bg-zinc-200" />

            <button
              type="button"
              onClick={() => {
                if (formData.clientName.trim()) setQuoteStep(2);
              }}
              className={`flex items-center gap-2 text-xs sm:text-sm font-bold transition-colors cursor-pointer ${
                quoteStep === 2 ? 'text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'
              }`}
            >
              <span className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                (formData.products.length > 0 || formData.services.length > 0 || formData.timeHours > 0) && quoteStep > 2
                  ? 'bg-emerald-600 text-white'
                  : quoteStep === 2
                  ? 'bg-zinc-900 text-white'
                  : 'bg-zinc-100 text-zinc-500 border border-zinc-200'
              }`}>
                {(formData.products.length > 0 || formData.services.length > 0 || formData.timeHours > 0) && quoteStep > 2 ? <Check className="w-4 h-4" /> : '2'}
              </span>
              <span>Itens</span>
            </button>

            <span className="w-6 sm:w-12 h-0.5 bg-zinc-200" />

            <button
              type="button"
              onClick={() => {
                if (formData.clientName.trim()) setQuoteStep(3);
              }}
              className={`flex items-center gap-2 text-xs sm:text-sm font-bold transition-colors cursor-pointer ${
                quoteStep === 3 ? 'text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'
              }`}
            >
              <span className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                quoteStep === 3
                  ? 'bg-zinc-900 text-white'
                  : 'bg-zinc-100 text-zinc-500 border border-zinc-200'
              }`}>
                3
              </span>
              <span>Revisar e enviar</span>
            </button>
          </div>
        </div>
      )}

      {/* CORPO PRINCIPAL */}
      <main className="flex-1 relative flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto w-full" ref={scrollRef}>
          <div className={`max-w-7xl mx-auto w-full flex flex-col lg:grid lg:grid-cols-12 lg:gap-8 p-3 sm:p-4 pb-32 lg:p-6 lg:pb-8 lg:items-start relative h-full ${isSharing ? 'pointer-events-none opacity-50' : ''}`}>
            
            {/* OVERLAY DE COMPARTILHAMENTO */}
            {isSharing && (
              <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
                <div className="bg-zinc-900/80 text-white px-6 py-3 rounded-full text-sm font-bold flex items-center gap-3 shadow-2xl backdrop-blur-md">
                  <div className="w-4 h-4 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                  Concluindo Compartilhamento...
                </div>
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════════ */}
            {/* MODO DIRETO (SEM IA) - WIZARD 3 PASSOS (TELA 03)                    */}
            {/* ═══════════════════════════════════════════════════════════════════ */}
            {quoteMode === 'direct' && (
              <div className="lg:col-span-7 flex flex-col space-y-4 pb-6">
                
                {/* ───────────────────────────────────────────────────────────── */}
                {/* PASSO 1: DADOS DO CLIENTE & PROPOSTA                          */}
                {/* ───────────────────────────────────────────────────────────── */}
                {quoteStep === 1 && (
                  <div className="bg-white rounded-3xl p-6 border border-zinc-100 shadow-xs space-y-5">
                    <div className="flex items-center justify-between pb-3 border-b border-zinc-100">
                      <div>
                        <h2 className="text-lg font-black text-zinc-900">1. Dados do Cliente</h2>
                        <p className="text-xs text-zinc-500">Para quem você está montando esta proposta</p>
                      </div>
                      <User className="w-5 h-5 text-zinc-400" />
                    </div>

                    <div className="space-y-4">
                      {/* Nome do Cliente com Autocompletar */}
                      <div className="relative">
                        <label className="block text-xs font-bold text-zinc-700 mb-1.5">Nome do Cliente *</label>
                        <input
                          type="text"
                          value={formData.clientName || ''}
                          onChange={e => {
                            setFormData(prev => ({ ...prev, clientName: e.target.value }));
                            setShowClientDropdown(true);
                          }}
                          onFocus={() => setShowClientDropdown(true)}
                          placeholder="Ex: Carlos Eduardo Silva"
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl px-4 py-3 text-sm font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 transition-all"
                        />
                        
                        {showClientDropdown && filteredClients.length > 0 && (formData.clientName || '').trim().length > 0 && (
                          <div className="absolute z-50 left-0 right-0 top-full mt-1.5 bg-white rounded-2xl shadow-xl border border-zinc-100 overflow-hidden">
                            <div className="p-1.5">
                              {filteredClients.map((c, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  onMouseDown={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      clientName: c.name,
                                      clientPhone: c.phone || prev.clientPhone || '',
                                      clientAddress: c.address || prev.clientAddress || ''
                                    }));
                                    setShowClientDropdown(false);
                                  }}
                                  className="w-full text-left p-3 hover:bg-zinc-50 rounded-xl text-xs flex items-center justify-between group cursor-pointer"
                                >
                                  <div>
                                    <p className="font-bold text-zinc-900">{c.name}</p>
                                    {c.phone && <p className="text-[11px] text-zinc-400">{c.phone}</p>}
                                  </div>
                                  <span className="text-[10px] text-zinc-700 font-bold bg-zinc-100 px-2.5 py-1 rounded-full">Selecionar</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* WhatsApp */}
                        <div>
                          <label className="block text-xs font-bold text-zinc-700 mb-1.5">WhatsApp / Telefone</label>
                          <input
                            type="text"
                            value={formData.clientPhone || ''}
                            onChange={e => setFormData(prev => ({ ...prev, clientPhone: e.target.value }))}
                            placeholder="(11) 98888-7777"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl px-4 py-3 text-sm font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 transition-all"
                          />
                        </div>

                        {/* Título do Serviço / Proposta */}
                        <div>
                          <label className="block text-xs font-bold text-zinc-700 mb-1.5">Título do Serviço / Proposta *</label>
                          <input
                            type="text"
                            value={formData.serviceName || ''}
                            onChange={e => setFormData(prev => ({ ...prev, serviceName: e.target.value }))}
                            placeholder="Ex: Consulta e Vacina V10"
                            className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl px-4 py-3 text-sm font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 transition-all"
                          />
                        </div>
                      </div>

                      {/* Endereço */}
                      <div>
                        <label className="block text-xs font-bold text-zinc-700 mb-1.5">Endereço / Local da Execução (Opcional)</label>
                        <input
                          type="text"
                          value={formData.clientAddress || ''}
                          onChange={e => setFormData(prev => ({ ...prev, clientAddress: e.target.value }))}
                          placeholder="Rua, número, bairro, cidade"
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl px-4 py-3 text-sm font-medium text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 transition-all"
                        />
                      </div>
                    </div>

                    <div className="pt-3 flex justify-end">
                      <button
                        type="button"
                        disabled={!formData.clientName?.trim()}
                        onClick={() => setQuoteStep(2)}
                        className="py-3 px-6 bg-zinc-900 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-sm flex items-center gap-2 transition-all cursor-pointer active:scale-95"
                      >
                        <span>Avançar para Itens</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* ───────────────────────────────────────────────────────────── */}
                {/* PASSO 2: ITENS DA PROPOSTA (SERVIÇOS, KITS & INSUMOS)         */}
                {/* ───────────────────────────────────────────────────────────── */}
                {quoteStep === 2 && (
                  <div className="bg-white rounded-3xl p-6 border border-zinc-100 shadow-xs space-y-6">
                    {/* Banner do Cliente Selecionado */}
                    <div className="flex justify-between items-center gap-4 pb-4 border-b border-zinc-100">
                      <div>
                        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block">Cliente Selecionado</span>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-lg font-black text-zinc-900">{formData.clientName || 'Cliente não informado'}</span>
                          {formData.serviceName && (
                            <span className="text-xs text-zinc-500 font-medium">· {formData.serviceName}</span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setQuoteStep(1)}
                        className="px-3.5 py-1.5 rounded-xl border border-zinc-200 text-xs font-bold text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
                      >
                        Trocar
                      </button>
                    </div>

                    {/* Atalhos Rápidos do Catálogo */}
                    {(catalog.services.length > 0 || catalog.recipes.length > 0 || catalog.products.length > 0) && (
                      <div className="bg-zinc-50/80 rounded-2xl p-4 border border-zinc-100 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-black text-zinc-800 uppercase tracking-wider">Atalhos do Catálogo</span>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => setCatalogFilter('all')}
                              className={`text-[10px] font-bold px-2.5 py-1 rounded-full transition-all cursor-pointer ${catalogFilter === 'all' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 border border-zinc-200'}`}
                            >
                              Todos
                            </button>
                            {catalog.services.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setCatalogFilter('services')}
                                className={`text-[10px] font-bold px-2.5 py-1 rounded-full transition-all cursor-pointer ${catalogFilter === 'services' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 border border-zinc-200'}`}
                              >
                                Serviços ({catalog.services.length})
                              </button>
                            )}
                            {catalog.recipes.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setCatalogFilter('recipes')}
                                className={`text-[10px] font-bold px-2.5 py-1 rounded-full transition-all cursor-pointer ${catalogFilter === 'recipes' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 border border-zinc-200'}`}
                              >
                                Kits ({catalog.recipes.length})
                              </button>
                            )}
                            {catalog.products.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setCatalogFilter('materials')}
                                className={`text-[10px] font-bold px-2.5 py-1 rounded-full transition-all cursor-pointer ${catalogFilter === 'materials' ? 'bg-zinc-900 text-white' : 'bg-white text-zinc-600 border border-zinc-200'}`}
                              >
                                Insumos ({catalog.products.length})
                              </button>
                            )}
                          </div>
                        </div>

                        <p className="text-[11px] text-zinc-500">Toque para adicionar diretamente à proposta:</p>

                        <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto pr-1">
                          {(catalogFilter === 'all' || catalogFilter === 'services') && catalog.services.map((s, i) => (
                            <button
                              key={`srv_${i}`}
                              type="button"
                              onClick={() => handleAddServiceFromCatalog(s)}
                              className="flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-800 px-3 py-1.5 rounded-xl text-xs font-bold transition-all active:scale-95 cursor-pointer shadow-2xs"
                            >
                              <Plus className="w-3.5 h-3.5 text-blue-600" />
                              <span>{s.name}</span>
                              <span className="text-[10px] font-mono text-blue-600 bg-white/80 px-1.5 py-0.5 rounded-md">{formatCurrencyBRL(s.value)}</span>
                            </button>
                          ))}

                          {(catalogFilter === 'all' || catalogFilter === 'recipes') && catalog.recipes.map((r, i) => (
                            <button
                              key={`rec_${i}`}
                              type="button"
                              onClick={() => loadRecipe(r.id)}
                              className="flex items-center gap-1.5 bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-800 px-3 py-1.5 rounded-xl text-xs font-bold transition-all active:scale-95 cursor-pointer shadow-2xs"
                            >
                              <Boxes className="w-3.5 h-3.5 text-purple-600" />
                              <span>Kit: {r.name}</span>
                              <span className="text-[10px] font-mono text-purple-600 bg-white/80 px-1.5 py-0.5 rounded-md">{formatCurrencyBRL(r.cost)}</span>
                            </button>
                          ))}

                          {(catalogFilter === 'all' || catalogFilter === 'materials') && catalog.products.filter(p => !p.recipeId).map((p, i) => (
                            <button
                              key={`mat_${i}`}
                              type="button"
                              onClick={() => handleAddProductFromCatalog(p)}
                              className="flex items-center gap-1.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-800 px-3 py-1.5 rounded-xl text-xs font-bold transition-all active:scale-95 cursor-pointer shadow-2xs"
                            >
                              <Plus className="w-3.5 h-3.5 text-emerald-600" />
                              <span>{p.name}</span>
                              <span className="text-[10px] font-mono text-emerald-600 bg-white/80 px-1.5 py-0.5 rounded-md">{formatCurrencyBRL(p.unitValue)}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Barra de Ações Rápidas de Adição */}
                    <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-black text-zinc-900 uppercase tracking-wider">Itens do Orçamento</span>
                        <span className="text-xs font-bold text-zinc-400">({formData.products.length + formData.services.length + ((formData.timeHours || formData.timeMinutes) ? 1 : 0)})</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={addService}
                          className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 font-bold text-xs rounded-xl flex items-center gap-1 transition-colors cursor-pointer"
                        >
                          <Plus className="w-3.5 h-3.5" /> Adicionar Serviço / Item
                        </button>
                        <button
                          type="button"
                          onClick={addProduct}
                          className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 font-bold text-xs rounded-xl flex items-center gap-1 transition-colors cursor-pointer"
                        >
                          <Plus className="w-3.5 h-3.5" /> Adicionar Insumo (Custo)
                        </button>
                      </div>
                    </div>

                    {/* Lista Unificada de Itens */}
                    <div className="space-y-3">
                      {/* Serviços */}
                      {formData.services.map((s, i) => (
                        <div key={`srv_item_${i}`} className="flex items-center gap-2 bg-zinc-50 p-3 rounded-2xl border border-zinc-100">
                          <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0" title="Serviço" />
                          <input
                            type="text"
                            value={s.name}
                            onChange={e => updateService(i, 'name', e.target.value)}
                            placeholder="Descrição do serviço"
                            className="flex-1 bg-white border border-zinc-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 outline-none focus:ring-1 focus:ring-zinc-900"
                          />
                          <div className="flex items-center bg-white border border-zinc-200 rounded-xl px-3 py-2 w-32 shrink-0">
                            <span className="text-[11px] text-zinc-400 mr-1 font-bold">R$</span>
                            <input
                              type="number"
                              value={s.value || ''}
                              onChange={e => updateService(i, 'value', Number(e.target.value))}
                              placeholder="0,00"
                              className="w-full bg-transparent text-xs font-mono font-bold text-zinc-900 outline-none text-right"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeService(i)}
                            className="text-zinc-400 hover:text-rose-500 p-2 transition-colors cursor-pointer shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}

                      {/* Produtos e Insumos */}
                      {formData.products.map((p, i) => (
                        <div key={`prod_item_${i}`} className="bg-zinc-50 p-3.5 rounded-2xl border border-zinc-100 space-y-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" title="Insumo / Material" />
                            <input
                              type="text"
                              value={p.name}
                              onChange={e => updateProduct(i, 'name', e.target.value)}
                              placeholder="Nome do produto ou insumo"
                              className="flex-1 bg-white border border-zinc-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 outline-none focus:ring-1 focus:ring-zinc-900"
                            />
                            <button
                              type="button"
                              onClick={() => removeProduct(i)}
                              className="text-zinc-400 hover:text-rose-500 p-2 transition-colors cursor-pointer shrink-0"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-12 gap-2">
                            <div className="col-span-3 flex items-center bg-white border border-zinc-200 rounded-xl px-2.5 py-1.5">
                              <span className="text-[10px] text-zinc-400 mr-1 uppercase font-bold">Qtd</span>
                              <input
                                type="number"
                                min="1"
                                value={p.quantity || ''}
                                onChange={e => updateProduct(i, 'quantity', Number(e.target.value))}
                                className="w-full bg-transparent text-xs font-bold outline-none text-center"
                              />
                            </div>
                            <div className="col-span-4 flex items-center bg-white border border-zinc-200 rounded-xl px-2.5 py-1.5">
                              <span className="text-[10px] text-zinc-400 mr-1 uppercase font-bold">Unit</span>
                              <input
                                type="number"
                                value={p.unitValue || ''}
                                onChange={e => updateProduct(i, 'unitValue', Number(e.target.value))}
                                placeholder="0,00"
                                className="w-full bg-transparent text-xs font-mono font-bold outline-none text-right"
                              />
                            </div>
                            <div className="col-span-5 flex items-center justify-end bg-zinc-100 rounded-xl px-3 py-1.5">
                              <span className="text-[10px] text-zinc-400 mr-1 uppercase font-bold">Total</span>
                              <span className="text-xs font-mono font-black text-zinc-900">{formatCurrencyBRL(p.total || 0)}</span>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-3 pt-1 border-t border-zinc-200/50">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!p.isMarkupHidden}
                                onChange={e => updateProduct(i, 'isMarkupHidden', e.target.checked)}
                                className="w-3.5 h-3.5 accent-brand rounded cursor-pointer"
                              />
                              <span className="text-[10px] font-bold text-zinc-600">Ocultar lucro no PDF</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={p.showOnPdf !== false}
                                onChange={e => updateProduct(i, 'showOnPdf', e.target.checked)}
                                className="w-3.5 h-3.5 accent-indigo-600 rounded cursor-pointer"
                              />
                              <span className="text-[10px] font-bold text-zinc-600">Exibir no PDF</span>
                            </label>
                            {isServiceNicheActive(formData.categoryIndex) && (
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={!!p.isPassThrough}
                                  onChange={e => updateProduct(i, 'isPassThrough', e.target.checked)}
                                  className="w-3.5 h-3.5 accent-brand rounded cursor-pointer"
                                />
                                <span className="text-[10px] font-semibold text-zinc-500">Repassar custo</span>
                              </label>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Mão de Obra e Tempo */}
                      <div className="bg-blue-50/40 border border-blue-100 rounded-2xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-blue-600" />
                            <span className="text-xs font-black text-blue-900 uppercase tracking-wider">Tempo de Execução (VHB)</span>
                          </div>
                          <span className="text-[11px] font-bold text-blue-600">
                            Sua Hora: {formatCurrencyBRL(config.baseHourValue || 50)}/h
                          </span>
                        </div>
                        <div className="grid grid-cols-12 gap-3 items-center">
                          <div className="col-span-4">
                            <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">Horas</label>
                            <input
                              type="number"
                              min="0"
                              value={formData.timeHours || ''}
                              onChange={e => setFormData(prev => ({ ...prev, timeHours: Math.max(0, parseInt(e.target.value) || 0), finalPrice: undefined }))}
                              placeholder="0h"
                              className="w-full bg-white border border-blue-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                          </div>
                          <div className="col-span-4">
                            <label className="block text-[10px] font-bold text-zinc-500 uppercase mb-1">Minutos</label>
                            <input
                              type="number"
                              min="0"
                              max="59"
                              step="5"
                              value={formData.timeMinutes || ''}
                              onChange={e => setFormData(prev => ({ ...prev, timeMinutes: Math.max(0, Math.min(59, parseInt(e.target.value) || 0)), finalPrice: undefined }))}
                              placeholder="0min"
                              className="w-full bg-white border border-blue-200 rounded-xl px-3 py-2 text-xs font-bold text-zinc-900 focus:outline-none focus:ring-1 focus:ring-blue-400"
                            />
                          </div>
                          <div className="col-span-4 bg-white border border-blue-200 rounded-xl p-2.5 text-right">
                            <span className="text-[9px] uppercase font-bold text-zinc-400 block">Total Mão de Obra</span>
                            <span className="text-xs font-mono font-black text-blue-700">{formatCurrencyBRL(laborCostFromTime)}</span>
                          </div>
                        </div>
                      </div>

                      {formData.services.length === 0 && formData.products.length === 0 && (formData.timeHours || 0) === 0 && (formData.timeMinutes || 0) === 0 && (
                        <div className="text-center py-8 bg-zinc-50 rounded-2xl border border-dashed border-zinc-200 space-y-3 p-4">
                          <Package className="w-8 h-8 text-zinc-300 mx-auto mb-1" />
                          <p className="text-xs font-bold text-zinc-700">Nenhum item adicionado ainda</p>
                          <p className="text-[11px] text-zinc-400 max-w-sm mx-auto">
                            Use os botões acima, atalhos do catálogo ou clique abaixo para lançar o item principal.
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              const sName = formData.serviceName.trim() || 'Item / Produto Principal';
                              setFormData(prev => ({
                                ...prev,
                                services: [...prev.services, { name: sName, value: 0 }]
                              }));
                            }}
                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-xl text-xs font-bold hover:bg-zinc-800 transition-colors cursor-pointer shadow-xs active:scale-95"
                          >
                            <Plus className="w-3.5 h-3.5 text-emerald-400" />
                            <span>Adicionar {formData.serviceName.trim() ? `"${formData.serviceName.trim()}"` : 'Primeiro Item'}</span>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Navegação do Passo 2 */}
                    <div className="pt-4 border-t border-zinc-100 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => setQuoteStep(1)}
                        className="py-3 px-5 border border-zinc-200 hover:bg-zinc-50 text-zinc-700 font-bold text-xs uppercase tracking-wider rounded-2xl transition-all cursor-pointer flex items-center gap-1.5"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span>Voltar para Cliente</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setQuoteStep(3)}
                        className="py-3 px-6 bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-sm flex items-center gap-2 transition-all cursor-pointer active:scale-95"
                      >
                        <span>Avançar para Revisão</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* ───────────────────────────────────────────────────────────── */}
                {/* PASSO 3: REVISAR E ENVIAR                                     */}
                {/* ───────────────────────────────────────────────────────────── */}
                {quoteStep === 3 && (
                  <div className="bg-white rounded-3xl p-6 border border-zinc-100 shadow-xs space-y-6">
                    <div className="flex items-center justify-between pb-4 border-b border-zinc-100">
                      <div>
                        <h2 className="text-lg font-black text-zinc-900">3. Revisar e Enviar</h2>
                        <p className="text-xs text-zinc-500 mt-0.5">Confira o resumo discriminado antes de emitir o PDF oficial.</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setQuoteStep(2)}
                        className="px-3.5 py-1.5 rounded-xl border border-zinc-200 text-xs font-bold text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
                      >
                        Editar Itens
                      </button>
                    </div>

                    {/* Resumo do Cliente e Obra */}
                    <div className="bg-zinc-50 rounded-2xl p-4 border border-zinc-100 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block">Cliente</span>
                        <span className="font-bold text-zinc-900 text-sm">{formData.clientName || 'Não informado'}</span>
                        {formData.clientPhone && <p className="text-[11px] text-zinc-500">{formData.clientPhone}</p>}
                      </div>
                      <div>
                        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block">Proposta / Serviço</span>
                        <span className="font-bold text-zinc-900 text-sm">{formData.serviceName || 'Orçamento de Serviços'}</span>
                        {formData.clientAddress && <p className="text-[11px] text-zinc-500 truncate">{formData.clientAddress}</p>}
                      </div>
                    </div>

                    {/* Resumo dos Itens Discriminados */}
                    <div className="space-y-2">
                      <span className="text-xs font-black text-zinc-900 uppercase tracking-wider block">Itens da Proposta</span>
                      <div className="border border-zinc-100 rounded-2xl overflow-hidden divide-y divide-zinc-100">
                        {formData.services.map((s, i) => (
                          <div key={`rev_srv_${i}`} className="p-3 bg-white flex justify-between items-center text-xs">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                              <span className="font-bold text-zinc-800">{s.name || 'Serviço sem nome'}</span>
                            </div>
                            <span className="font-mono font-bold text-zinc-900">{formatCurrencyBRL(s.value)}</span>
                          </div>
                        ))}
                        {formData.products.map((p, i) => (
                          <div key={`rev_prod_${i}`} className="p-3 bg-white flex justify-between items-center text-xs">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                              <span className="font-bold text-zinc-800">{p.name || 'Material sem nome'}</span>
                              <span className="text-[11px] text-zinc-400 font-mono">({p.quantity}x {formatCurrencyBRL(p.unitValue)})</span>
                            </div>
                            <span className="font-mono font-bold text-zinc-900">{formatCurrencyBRL(p.total)}</span>
                          </div>
                        ))}
                        {laborCostFromTime > 0 && (
                          <div className="p-3 bg-white flex justify-between items-center text-xs">
                            <div className="flex items-center gap-2">
                              <Clock className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                              <span className="font-bold text-zinc-800">Mão de Obra ({formData.timeHours || 0}h {formData.timeMinutes || 0}min)</span>
                            </div>
                            <span className="font-mono font-bold text-blue-700">{formatCurrencyBRL(laborCostFromTime)}</span>
                          </div>
                        )}
                        {operationalDifference > 0.50 && (
                          <div className="p-3 bg-zinc-50 flex justify-between items-center text-xs">
                            <div>
                              <p className="font-bold text-zinc-800">Custos Operacionais e Logística</p>
                              <p className="text-[10px] text-zinc-500">Gestão, Margem de Segurança e Reserva</p>
                            </div>
                            <span className="font-mono font-bold text-zinc-900">{formatCurrencyBRL(operationalDifference)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Alerta Constitucional Artigo 2 §2 (Soma > finalPrice) */}
                    {isSumExceedingFinalPrice && (
                      <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl flex gap-3 text-rose-800">
                        <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                        <div className="text-xs">
                          <p className="font-black text-rose-900 uppercase tracking-wide">Atenção: Soma dos Itens Excede o Preço Final</p>
                          <p className="text-rose-700 mt-1 leading-relaxed">
                            A soma dos itens discriminados ({formatCurrencyBRL(itemsSum)}) não pode ultrapassar o valor final ({formatCurrencyBRL(finalPrice)}).
                            Ajuste os valores dos itens ou o preço sugerido ao lado para liberar a emissão do PDF.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Prazo e Observações */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-zinc-700 mb-1.5">Prazo de Validade / Execução</label>
                        <input
                          type="text"
                          value={formData.deadline || ''}
                          onChange={e => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
                          placeholder="Ex: Proposta válida por 15 dias"
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl px-4 py-2.5 text-xs font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-zinc-700 mb-1.5">Condições de Pagamento</label>
                        <input
                          type="text"
                          value={formData.serviceDescription || ''}
                          onChange={e => setFormData(prev => ({ ...prev, serviceDescription: e.target.value }))}
                          placeholder="Ex: 50% de entrada + 50% na entrega"
                          className="w-full bg-zinc-50 border border-zinc-200 rounded-2xl px-4 py-2.5 text-xs font-bold text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900"
                        />
                      </div>
                    </div>

                    <div className="pt-4 border-t border-zinc-100 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => setQuoteStep(2)}
                        className="py-3 px-5 border border-zinc-200 hover:bg-zinc-50 text-zinc-700 font-bold text-xs uppercase tracking-wider rounded-2xl transition-all cursor-pointer flex items-center gap-1.5"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        <span>Voltar para Itens</span>
                      </button>
                      <div className="text-right">
                        <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider block">Total Calculado</span>
                        <span className="text-xl font-black text-zinc-900">{formatCurrencyBRL(finalPrice)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════════ */}
            {/* MODO IA (ASSISTENTE CONVERSACIONAL)                                 */}
            {/* ═══════════════════════════════════════════════════════════════════ */}
            {quoteMode === 'ai' && (
              <div className={`${budgetReady ? 'lg:col-span-7' : 'lg:col-span-12 max-w-4xl mx-auto w-full'} flex flex-col space-y-4 min-h-[40vh] lg:min-h-full pb-8 lg:pb-8`}>
                <ChatAssistant
                  key={chatSessionKey}
                  user={user}
                  config={config}
                  materials={materials}
                  recipes={recipes}
                  services={services}
                  initialFormData={formData}
                  quotesCount={quotesCount}
                  plan={plan}
                  onFormDataUpdate={(patch) => {
                    setFormData(prev => {
                      const updated = { ...prev, ...patch };
                      
                      if (patch.clientName && typeof patch.clientName === 'string' && patch.clientName.trim() !== '') {
                        updated.clientName = patch.clientName.trim();
                      }
                      
                      if (Array.isArray(patch.matchedServices)) {
                        updated.services = patch.matchedServices.map(s => ({
                          name: s.name,
                          value: Number(s.value || 0) * (Number(s.quantity) || 1)
                        }));
                      }
                      
                      if (Array.isArray(patch.matchedProducts)) {
                        updated.products = patch.matchedProducts.map(p => {
                          const qty = Number(p.quantity) || 1;
                          const uVal = Number(p.unitValue) || 0;
                          const matchedMat = materials.find(m => m.name.toLowerCase().trim() === (p.name || '').toLowerCase().trim());
                          return {
                            name: p.name,
                            unitValue: uVal,
                            quantity: qty,
                            total: uVal * qty,
                            materialId: matchedMat?.id,
                            isPassThrough: false,
                            showOnPdf: true
                          };
                        });
                      }
                      
                      if (patch.timeSpentMinutes !== undefined && patch.timeSpentMinutes !== null) {
                        const totalMin = Number(patch.timeSpentMinutes);
                        updated.timeHours = Math.floor(totalMin / 60);
                        updated.timeMinutes = totalMin % 60;
                      }
                      
                      if (patch.finalPrice !== undefined) {
                        updated.finalPrice = (patch.finalPrice !== null && Number(patch.finalPrice) > 0) ? Number(patch.finalPrice) : undefined;
                      }
                      
                      return updated;
                    });

                    if ((patch.services && patch.services.length > 0) || 
                        (patch.matchedProducts && patch.matchedProducts.length > 0) || 
                        (patch.finalPrice && Number(patch.finalPrice) > 0)) {
                      setBudgetReady(true);
                    }
                  }}
                  onAiExplanation={(ex) => setAiExplanation(ex)}
                  onBudgetReady={(ready) => setBudgetReady(ready)}
                  onOpenProfile={onOpenProfile}
                  onOpenCosts={onOpenCosts}
                  onOpenHistory={onOpenHistory}
                />
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════════ */}
            {/* PAINEL FINANCEIRO, TERMÔMETRO & EXPORTAÇÃO (EM AMBOS OS MODOS)       */}
            {/* ═══════════════════════════════════════════════════════════════════ */}
            {(budgetReady || quoteMode === 'direct') && (
              <div className="lg:col-span-5 mb-6 lg:mb-0">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                  <div className="bg-white rounded-2xl p-4 lg:p-6 border border-zinc-100 shadow-sm lg:max-h-[85vh] lg:overflow-y-auto custom-scrollbar">
                    
                    {/* Header do Card Financeiro */}
                    <div className="text-center mb-4 lg:mb-5">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <p className="text-zinc-400 text-[10px] uppercase font-bold tracking-widest">
                          {formData.finalPrice !== undefined && formData.finalPrice > 0 ? 'Preço Fixado Manualmente' : 'Preço Final'}
                        </p>
                        {formData.finalPrice !== undefined && formData.finalPrice > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, finalPrice: undefined }));
                              setIsEditingFinalPrice(false);
                            }}
                            className="text-[9px] text-brand hover:underline font-bold bg-brand/10 px-2 py-0.5 rounded-full transition-all cursor-pointer"
                            title="Recalcular Preço Sugerido dinâmico"
                          >
                            Recalcular
                          </button>
                        )}
                      </div>

                      {isEditingFinalPrice ? (
                        <div className="flex items-center justify-center gap-1.5 my-2">
                          <span className="text-base font-bold text-zinc-400">R$</span>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            autoFocus
                            value={manualPriceInput}
                            onChange={e => setManualPriceInput(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleApplyManualFinalPrice();
                              if (e.key === 'Escape') setIsEditingFinalPrice(false);
                            }}
                            placeholder="0,00"
                            className="w-28 text-center text-2xl font-black text-zinc-900 border-b-2 border-zinc-900 bg-transparent outline-none pb-0.5"
                          />
                          <button
                            type="button"
                            onClick={handleApplyManualFinalPrice}
                            className="px-2.5 py-1 bg-zinc-900 text-white rounded-lg text-xs font-bold hover:bg-zinc-800 transition-colors cursor-pointer"
                          >
                            Salvar
                          </button>
                          <button
                            type="button"
                            onClick={() => setIsEditingFinalPrice(false)}
                            className="px-2 py-1 text-zinc-400 hover:text-zinc-600 text-xs font-bold transition-colors cursor-pointer"
                          >
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <div className="group relative inline-flex items-center justify-center gap-2">
                          <h2
                            onClick={() => {
                              setManualPriceInput(finalPrice > 0 ? String(finalPrice) : '');
                              setIsEditingFinalPrice(true);
                            }}
                            className={`text-3xl lg:text-4xl font-black tracking-tight cursor-pointer hover:opacity-80 transition-opacity ${
                              hasMathError ? 'text-amber-500' : 'text-zinc-900'
                            }`}
                            title="Toque para editar o preço final"
                          >
                            {formatCurrencyBRL(finalPrice)}
                          </h2>
                          <button
                            type="button"
                            onClick={() => {
                              setManualPriceInput(finalPrice > 0 ? String(finalPrice) : '');
                              setIsEditingFinalPrice(true);
                            }}
                            className="text-zinc-400 hover:text-zinc-700 p-1 rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer"
                            title="Editar Preço Final"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}

                      {/* Chip de Margem Saudável ou Meta de Produtividade */}
                      <div className="mt-2 flex justify-center">
                        {productivityBreakEven ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-sky-50 text-sky-700 border border-sky-200">
                            ⚡ Meta: {productivityBreakEven.minItemsPerHour} {productivityBreakEven.unitLabel}/h
                          </span>
                        ) : (
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                            hasMathError 
                              ? 'bg-amber-50 text-amber-700 border border-amber-200'
                              : displayMargin >= 20 
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-zinc-100 text-zinc-600 border border-zinc-200'
                          }`}>
                            {hasMathError ? 'Atenção na margem' : `Margem de ${Math.round(displayMargin)}%, ${displayMargin >= 20 ? 'saudável' : 'baixa'}`}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Resumo Custo e Lucro */}
                    <div className="space-y-2.5 py-4 border-y border-zinc-100 mb-4">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-zinc-500 font-medium">Seu custo</span>
                        <span className="font-mono font-bold text-zinc-900">{formatCurrencyBRL(costPrice)}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-zinc-500 font-medium">Seu lucro</span>
                        <span className={`font-mono font-black ${hasMathError ? 'text-amber-500' : 'text-emerald-600'}`}>
                          {formatCurrencyBRL(effectiveProfit)}
                        </span>
                      </div>
                    </div>

                    {/* Card de Meta de Produtividade (Break-Even) */}
                    {productivityBreakEven && (
                      <div className="bg-sky-50 border border-sky-200 rounded-2xl p-4 mb-4 text-left shadow-xs">
                        <div className="flex items-start gap-2.5">
                          <span className="text-sky-600 font-bold text-sm mt-0.5">⚡</span>
                          <div className="flex-1">
                            <p className="text-[10px] font-black text-sky-800 uppercase tracking-wider mb-1">
                              Meta de Produtividade (Ponto de Equilíbrio)
                            </p>
                            <p className="text-xs text-sky-950 font-medium leading-relaxed">
                              Cobrando <strong>{formatCurrencyBRL(productivityBreakEven.unitVal)}/{productivityBreakEven.unitLabel}</strong>, para cobrir sua taxa de <strong>{formatCurrencyBRL(productivityBreakEven.vhb)}/h</strong> você precisa produzir ao menos <strong>{productivityBreakEven.minItemsPerHour} {productivityBreakEven.unitLabelPlural} por hora</strong> (máximo de {productivityBreakEven.maxTimeFormatted} por {productivityBreakEven.unitLabel}).
                            </p>
                            <p className="text-[11px] text-sky-700 mt-1.5 font-semibold">
                              💡 A um ritmo de 1 min por {productivityBreakEven.unitLabel} (60 un/h), você fatura {formatCurrencyBRL(productivityBreakEven.dynamicHourlyRevenue)}/h ({productivityBreakEven.netSpreadPerHour >= 0 ? `+${formatCurrencyBRL(productivityBreakEven.netSpreadPerHour)} de lucro sobre a hora base` : 'abaixo da taxa base'}).
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Alertas de Margem ou Risco */}
                    {hasMathError && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex gap-3 shadow-xs">
                        <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-left flex-1">
                          <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest leading-none mb-1">
                            Alerta de Margem Irreal
                          </p>
                          <p className="text-[10px] text-amber-900 leading-snug font-medium">
                            Sua Margem Desejada somada aos impostos e taxas é maior ou igual a 100%. Ajuste o percentual no controle deslizante.
                          </p>
                        </div>
                      </div>
                    )}

                    {!hasMathError && riskAlert && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex gap-3 shadow-xs">
                        <AlertCircle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-left flex-1">
                          <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest leading-none mb-1">
                            {riskAlert.title}
                          </p>
                          <p className="text-[10px] text-amber-900 leading-snug mb-2 font-medium">
                            {riskAlert.message}
                          </p>
                          <button 
                            type="button"
                            onClick={riskAlert.action}
                            className="text-[10px] bg-amber-600 text-white px-3 py-1 rounded-full font-bold hover:bg-amber-700 transition-colors shadow-xs cursor-pointer"
                          >
                            {riskAlert.actionLabel}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Controle Deslizante de Margem (se permitido) */}
                    {canViewMargin ? (
                      <div className="space-y-2 bg-zinc-50 p-3.5 rounded-2xl border border-zinc-100 mb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <span className="text-[10px] font-bold text-zinc-500 uppercase">Margem de Lucro Desejada</span>
                            <Tooltip text="Percentual de gordura e lucro para crescimento do negócio.">
                              <Info className="w-3.5 h-3.5 text-zinc-400 cursor-help" />
                            </Tooltip>
                          </div>
                          <span className="text-xs font-black text-emerald-700 bg-white px-2 py-0.5 rounded-lg border border-zinc-200">
                            {formData.profitMarginInput}%
                          </span>
                        </div>
                        <input 
                          type="range" 
                          min="0" 
                          max="200" 
                          step="5" 
                          disabled={!canEditFinalPrice}
                          value={formData.profitMarginInput} 
                          onChange={e => setFormData(p => ({ ...p, profitMarginInput: Number(e.target.value), finalPrice: undefined }))} 
                          className="w-full accent-emerald-600 h-1.5 bg-zinc-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50" 
                        />
                      </div>
                    ) : (
                      <div className="p-3 bg-zinc-50 border border-zinc-100 rounded-xl mb-4 text-center">
                        <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest flex items-center justify-center gap-1.5">
                          <Lock className="w-3 h-3 text-zinc-400" />
                          Margem Protegida
                        </span>
                      </div>
                    )}

                    {/* Ações contextuais por modo e passo */}
                    {quoteMode === 'direct' && quoteStep === 1 && (
                      <div className="space-y-2 pt-2">
                        <button
                          type="button"
                          disabled={!formData.clientName?.trim()}
                          onClick={() => setQuoteStep(2)}
                          className="w-full py-3.5 bg-zinc-900 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-sm flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-95"
                        >
                          <span>Avançar para Itens</span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    )}

                    {quoteMode === 'direct' && quoteStep === 2 && (
                      <div className="space-y-2 pt-2">
                        <button
                          type="button"
                          onClick={() => setQuoteStep(3)}
                          className="w-full py-3.5 bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-sm flex items-center justify-center gap-2 transition-all cursor-pointer active:scale-95"
                        >
                          <span>Revisar e enviar</span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveQuote}
                          disabled={isSaving}
                          className="w-full py-3 bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-700 font-bold text-xs uppercase tracking-wider rounded-2xl transition-all cursor-pointer"
                        >
                          {isSaving ? 'Salvando...' : 'Salvar rascunho'}
                        </button>
                      </div>
                    )}

                    {(quoteMode === 'ai' || (quoteMode === 'direct' && quoteStep === 3)) && (
                      <div className="mt-2">
                        {isSumExceedingFinalPrice ? (
                          <div className="p-3 bg-rose-50 border border-rose-200 rounded-2xl text-center space-y-2">
                            <p className="text-xs font-bold text-rose-700">Emissão bloqueada</p>
                            <p className="text-[11px] text-rose-600">A soma dos itens supera o valor final. Corrija os itens ou o preço na aba anterior.</p>
                            <button
                              type="button"
                              onClick={() => setQuoteStep(2)}
                              className="w-full py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer"
                            >
                              Corrigir Itens
                            </button>
                          </div>
                        ) : (
                          <Suspense fallback={<LoadingSpinner />}>
                            <PDFExporter
                              user={user}
                              formData={formData}
                              finalPrice={finalPrice}
                              quoteCount={quotesCount}
                              templateType={CATEGORIES.find(c => c.id === formData.categoryIndex)?.templateType}
                              saved={saved}
                              hiddenMarkupTotal={hiddenMarkupTotal}
                              useOperationalTax={useOperationalTax}
                              isDemo={isDemo}
                              onShowPreview={() => setShowDemoPreview(true)}
                              onBeforeExport={() => {
                                handlePrepareExport();
                                setIsSharing(true);
                              }}
                              onSave={handleSaveQuote}
                              onAfterExport={() => {
                                setIsSharing(false);
                              }}
                              onOpenHistory={onOpenHistory}
                              onResetForm={() => handleHardReset(true)}
                            />
                          </Suspense>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>
            )}

          </div>
        </div>
      </main>

      {/* TOAST DE RESET / SUCESSO */}
      <AnimatePresence>
        {showResetToast && (
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-24 left-4 right-4 z-50 flex justify-center pointer-events-none"
          >
            <div className="bg-brand text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-3 border border-white/20 backdrop-blur-md">
              <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4" />
              </div>
              <div>
                <p className="font-bold text-xs">Formulário Pronto!</p>
                <p className="text-[10px] opacity-90 font-medium">Novo orçamento pronto para cálculo.</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <DemoPdfPreviewModal
        isOpen={showDemoPreview}
        onClose={() => setShowDemoPreview(false)}
        onCompleteSignup={() => {
          setShowDemoPreview(false);
          if (user.id === 'guest') {
            navigate('/auth?mode=signup');
          } else {
            if (onOpenProfile) onOpenProfile('business');
          }
        }}
        formData={formData}
        finalPrice={finalPrice}
      />

      <PaywallModal 
        isOpen={isPaywallOpen} 
        onClose={() => setIsPaywallOpen(false)} 
      />
    </div>
  );
}
